import { animate, fadeIn, flyBelow, type Options } from "@lit-labs/motion";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { repeat } from "lit/directives/repeat.js";
import { styles } from "./creator-follow.css";

import "@phosphor-icons/webcomponents/PhCheckCircle";
import "@phosphor-icons/webcomponents/PhPlus";

import { getAssetUrl } from "helpers/assetHelpers";
import { SignalElement, inject } from "ui-common/di";
import type { IExploreCreatorsState, Creator } from "ui-common/services/explore-creators/types";
import type { IExploreCreatorsService } from "ui-common/services/explore-creators/types";

import "ui-components/button/wow-button.ts";
import "ui-components/form-elements/input/wow-input.ts";
import "ui-components/skeleton-rect";
import { t } from "../../translations/i18n";

@customElement("creator-follow")
export class CreatorFollow extends SignalElement {
  static override styles = [styles];

  @inject<IExploreCreatorsState>("exploreCreatorsState")
  private creatorsState!: IExploreCreatorsState;

  @inject<IExploreCreatorsService>("exploreCreatorsService")
  private creatorsService!: IExploreCreatorsService;

  @property({ type: Array })
  followedCreators: string[] = [];

  @property({ type: Array })
  categoryIds: string[] = [];

  @state()
  private selectedCreatorsIds = new Set<string>();

  @state() private selectedCreators: Creator[] = [];

  @state()
  private searchTerm = "";

  @state()
  private imageErrors = new Set<string>();

  private readonly LIMIT = 20;
  private searchDebounceTimeout?: ReturnType<typeof setTimeout>;

  override connectedCallback() {
    super.connectedCallback();
    this.creatorsService.loadCreators(this.LIMIT, 0, false, this.searchTerm || undefined);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);

    if (changedProperties.has("searchTerm") || changedProperties.has("categoryIds")) {
      this.creatorsService.refetch(this.searchTerm || undefined);
    }
  }

  private handleSearch(e: Event) {
    const input = e.target as HTMLInputElement;
    const newValue = input.value;

    // Debounce the search to avoid too many requests
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }

    this.searchDebounceTimeout = setTimeout(() => {
      this.searchTerm = newValue;
    }, 300);
  }

  private handleCreatorToggle(creatorId: string) {
    const creatorsData = this.creatorsState.creators.get();
    const creators = creatorsData?.influencers ?? [];
    const creator = creators.find((c) => c.id === creatorId);

    if (this.selectedCreatorsIds.has(creatorId)) {
      this.selectedCreatorsIds.delete(creatorId);
      this.selectedCreators = this.selectedCreators.filter((c) => c.id !== creatorId);
    } else {
      this.selectedCreatorsIds.add(creatorId);

      // Add full creator data if found, otherwise keep existing info
      if (creator) {
        // Avoid duplicates
        const exists = this.selectedCreators.some((c) => c.id === creatorId);
        if (!exists) {
          this.selectedCreators = [...this.selectedCreators, creator];
        }
      }
    }

    this.requestUpdate();
    this.dispatchFollowEvent();
  }

  private dispatchFollowEvent() {
    const selectedHandles = this.selectedCreators.map((creator) => creator.handle).filter((handle) => handle !== null);

    this.dispatchEvent(
      new CustomEvent("creators-selected", {
        detail: { creators: selectedHandles },
        bubbles: true,
      }),
    );
  }

  private handleImageError(creatorId: string) {
    this.imageErrors = new Set(this.imageErrors).add(creatorId);
  }

  private hasImageError(creatorId: string): boolean {
    return this.imageErrors.has(creatorId);
  }

  private renderCreatorCard(creator: Creator, index: number) {
    const isSelected = this.selectedCreatorsIds.has(creator.id);
    const profileImageUrl = creator.profileImagePath || creator.profileImage;
    const imageUrl = profileImageUrl ? getAssetUrl(profileImageUrl) : undefined;

    const classes = { "creator-card": true, "creator-card-selected": isSelected };
    const animationConfig: Options = {
      in: fadeIn,
      out: flyBelow,
      keyframeOptions: {
        duration: 250,
        delay: index * 30,
        easing: "ease-in-out",
        fill: "both",
      },
    };

    return html`
      <div class=${classMap(classes)} @click=${() => this.handleCreatorToggle(creator.id)} ${animate(animationConfig)}>
        <div class="creator-card-image">
          ${imageUrl
            ? html`<img
                  src=${imageUrl}
                  alt=${creator.handle || "Creator"}
                  @error=${() => this.handleImageError(creator.id)}
                  style="display: ${this.hasImageError(creator.id) ? "none" : "block"}"
                />
                ${this.hasImageError(creator.id)
                  ? html`<div class="creator-card-placeholder">${creator.handle?.charAt(0)?.toUpperCase() || "?"}</div>`
                  : nothing}`
            : html`<div class="creator-card-placeholder">${creator.handle?.charAt(0)?.toUpperCase() || "?"}</div>`}
        </div>
        <div class="creator-card-info">
          <div class="creator-card-handle">@${creator.handle || "unknown"}</div>
        </div>
        <div class="creator-card-follow">
          <div class="follow-action">
            ${isSelected
              ? html`<ph-check-circle weight="bold"></ph-check-circle>`
              : html`
                  <ph-plus></ph-plus>
                  <span>Add</span>
                `}
          </div>
        </div>
      </div>
    `;
  }

  private renderCreatorList() {
    const creatorsData = this.creatorsState.creators.get();
    const creators = creatorsData?.influencers ?? [];
    const isLoading = this.creatorsState.loading.get();

    const loadingTemplate = repeat(
      Array(2),
      () => html`<skeleton-rect class="skeleton-rectangle" width="100%" height="56px"></skeleton-rect>`,
    );

    // Combine selected creators (persisted) with fetched creators
    const combinedCreatorsMap = new Map<string, Creator>();

    // Add selected first
    for (const creator of this.selectedCreators) {
      combinedCreatorsMap.set(creator.id, creator);
    }

    // Add others (avoid overwriting)
    for (const creator of creators) {
      if (!combinedCreatorsMap.has(creator.id)) {
        combinedCreatorsMap.set(creator.id, creator);
      }
    }

    // Get combined list
    const combinedCreators = Array.from(combinedCreatorsMap.values());

    // Sort: selected creators first
    combinedCreators.sort((a, b) => {
      const aSelected = this.selectedCreatorsIds.has(a.id);
      const bSelected = this.selectedCreatorsIds.has(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });

    if (combinedCreators.length === 0 && !isLoading) {
      return html`
        <div class="creator-follow-empty">
          <div class="creator-follow-empty-title">
            ${this.searchTerm ? "No creators found" : "No creators available"}
          </div>
          <div class="creator-follow-empty-subtitle">
            ${this.searchTerm
              ? "Try adjusting your search terms or explore different keywords"
              : "Check back later for new creators to follow"}
          </div>
        </div>
      `;
    }

    return html`
      <div class="creator-follow-list">
        ${repeat(
          combinedCreators,
          (creator) => creator.id,
          (creator, index) => this.renderCreatorCard(creator, index),
        )}
        ${isLoading ? loadingTemplate : nothing}
      </div>
    `;
  }

  protected override render() {
    return html`
      <div class="creator-follow">
        <div class="creator-follow-search">
          <wow-input placeholder=${t("sign-up.searchCreators")} @input=${this.handleSearch} .value=${this.searchTerm}>
          </wow-input>
        </div>

        ${this.renderCreatorList()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "creator-follow": CreatorFollow;
  }
}
